/* eslint-disable no-use-before-define */
import React, {useEffect} from "react";
import {Link as RouterLink, matchPath, useLocation} from "react-router-dom";
import PerfectScrollbar from "react-perfect-scrollbar";
import PropTypes from "prop-types";
import {Avatar, Box, Divider, Drawer, Hidden, List, ListSubheader, makeStyles, Typography} from "@material-ui/core";
import {Box as TenantIcon, Layers as OrganizationIcon, List as OrderIcon} from "react-feather";
import Logo from "src/components/Logo";
import useAuth from "src/hooks/useAuth";
import NavItem from "./NavItem";
import {useTranslation} from "react-i18next";
import {URL_TEMPLATES as URL} from "src/urlTemplates";

function renderNavItems({items, pathname, depth = 0}) {
    return (
        <List disablePadding>{items.reduce((acc, item) => reduceChildRoutes({acc, item, pathname, depth}), [])}</List>
    );
}

function reduceChildRoutes({acc, pathname, item, depth}) {
    const key = item.title + depth;

    if (item.items) {
        const open = matchPath(pathname, {
            path: item.href,
            exact: false
        });

        acc.push(
            <NavItem depth={depth} icon={item.icon} info={item.info} key={key} open={Boolean(open)} title={item.title}>
                {renderNavItems({
                    depth: depth + 1,
                    pathname,
                    items: item.items
                })}
            </NavItem>
        );
    } else {
        acc.push(<NavItem depth={depth} href={item.href} icon={item.icon} info={item.info} key={key} title={item.title}/>);
    }

    return acc;
}

const useStyles = makeStyles(() => ({
    mobileDrawer: {
        width: 256
    },
    desktopDrawer: {
        width: 256,
        top: 64,
        height: "calc(100% - 64px)"
    },
    avatar: {
        cursor: "pointer",
        width: 64,
        height: 64
    }
}));

const NavBar = ({onMobileClose, openMobile}) => {
    const classes = useStyles();
    const location = useLocation();
    const {user, hasAdminRole} = useAuth();
    const {t} = useTranslation("sections");

    const adminSections = [
        {
            subheader: t("management.section"),
            items: [
                {
                    title: t("management.items.tenantsAndUsers"),
                    icon: TenantIcon,
                    href: URL.TENANT
                }
            ]
        }
    ];

    const userSections = [
        {
            subheader: t("certificate.section"),
            items: [
                {
                    title: t("certificate.items.orders"),
                    icon: OrderIcon,
                    href: URL.ORDER
                },
                {
                    title: t("certificate.items.organizations"),
                    icon: OrganizationIcon,
                    href: URL.ORGANISATION
                }
            ]
        }
    ];

    useEffect(() => {
        if (openMobile && onMobileClose) {
            onMobileClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const content = (
        <Box height="100%" display="flex" flexDirection="column">
            <PerfectScrollbar options={{suppressScrollX: true}}>
                <Hidden lgUp>
                    <Box p={2} display="flex" justifyContent="center">
                        <RouterLink to="/">
                            <Logo/>
                        </RouterLink>
                    </Box>
                </Hidden>
                <Box p={2}>
                    <Box display="flex" justifyContent="center">
                        <RouterLink to={URL.ACCOUNT}>
                            <Avatar alt="User" className={classes.avatar} src={user.avatar}/>
                        </RouterLink>
                    </Box>
                    <Box mt={2} textAlign="center">
                        <Typography variant="h5" color="textPrimary" underline="none">
                            {user.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {user.company}
                        </Typography>
                    </Box>
                </Box>
                <Divider/>
                <Box p={2}>
                    {userSections.map(section => (
                        <List
                            key={section.subheader}
                            subheader={
                                <ListSubheader disableGutters disableSticky>
                                    {section.subheader}
                                </ListSubheader>
                            }
                        >
                            {renderNavItems({
                                items: section.items,
                                pathname: location.pathname
                            })}
                        </List>
                    ))}

                    {hasAdminRole() &&
                        adminSections.map(section => (
                            <List
                                key={section.subheader}
                                subheader={
                                    <ListSubheader disableGutters disableSticky>
                                        {section.subheader}
                                    </ListSubheader>
                                }
                            >
                                {renderNavItems({
                                    items: section.items,
                                    pathname: location.pathname
                                })}
                            </List>
                        ))}
                </Box>
            </PerfectScrollbar>
        </Box>
    );

    return (
        <>
            <Hidden lgUp>
                <Drawer
                    anchor="left"
                    classes={{paper: classes.mobileDrawer}}
                    onClose={onMobileClose}
                    open={openMobile}
                    variant="temporary"
                >
                    {content}
                </Drawer>
            </Hidden>
            <Hidden mdDown>
                <Drawer anchor="left" classes={{paper: classes.desktopDrawer}} open variant="persistent">
                    {content}
                </Drawer>
            </Hidden>
        </>
    );
};

NavBar.propTypes = {
    onMobileClose: PropTypes.func,
    openMobile: PropTypes.bool
};

export default NavBar;
